import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Moodboard, SaveToMoodboardPayload, MoodboardItem } from '@/types/moodboards';

export function useMoodboards(userId?: string) {
  return useQuery({
    queryKey: ['moodboards', userId],
    queryFn: async () => {
      let query = supabase
        .from('moodboards')
        .select('*, moodboard_items(count)')
        .order('updated_at', { ascending: false });

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map(m => ({
        ...m,
        item_count: m.moodboard_items?.[0]?.count || 0,
        moodboard_items: undefined
      })) as Moodboard[];
    },
    enabled: !!userId,
  });
}

export function useMoodboard(moodboardId?: string) {
  return useQuery({
    queryKey: ['moodboard', moodboardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('moodboards')
        .select('*')
        .eq('id', moodboardId!)
        .single();

      if (error) throw error;
      return data as Moodboard;
    },
    enabled: !!moodboardId,
  });
}

export function useCreateMoodboard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      name: string;
      icon?: string;
      is_private?: boolean;
      initialItem?: SaveToMoodboardPayload;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: moodboard, error } = await supabase
        .from('moodboards')
        .insert({
          user_id: user.id,
          name: params.name,
          icon: params.icon || '🎨',
          is_private: params.is_private || false,
        })
        .select()
        .single();

      if (error) throw error;

      // Add initial item if provided
      if (params.initialItem && moodboard) {
        const { error: itemError } = await supabase
          .from('moodboard_items')
          .insert({
            moodboard_id: moodboard.id,
            content_type: params.initialItem.content_type,
            content_id: params.initialItem.content_id,
            position: 0,
            image_url: params.initialItem.image_url,
            author_id: params.initialItem.author_id,
            author_username: params.initialItem.author_username,
            caption: params.initialItem.caption,
            original_created_at: params.initialItem.original_created_at,
          });

        if (itemError) throw itemError;

        // Update cover image
        await supabase
          .from('moodboards')
          .update({
            cover_image_url: params.initialItem.image_url,
            cover_content_type: params.initialItem.content_type,
            cover_content_id: params.initialItem.content_id,
          })
          .eq('id', moodboard.id);
      }

      return moodboard as Moodboard;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moodboards'] });
      toast({
        title: 'Moodboard created',
        description: 'Your new moodboard is ready!',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to create moodboard',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateMoodboard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      name?: string;
      icon?: string;
      is_private?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('moodboards')
        .update({
          name: params.name,
          icon: params.icon,
          is_private: params.is_private,
        })
        .eq('id', params.id)
        .select()
        .single();

      if (error) throw error;
      return data as Moodboard;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['moodboards'] });
      queryClient.invalidateQueries({ queryKey: ['moodboard', data.id] });
      toast({
        title: 'Moodboard updated',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update moodboard',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteMoodboard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (moodboardId: string) => {
      const { error } = await supabase
        .from('moodboards')
        .delete()
        .eq('id', moodboardId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moodboards'] });
      toast({
        title: 'Moodboard deleted',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete moodboard',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRecentMoodboards(limit = 5) {
  return useQuery({
    queryKey: ['moodboards', 'recent', limit],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('moodboards')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as Moodboard[];
    },
  });
}

// Check if content is saved to any moodboard
export function useIsItemSaved(contentType?: string, contentId?: string) {
  return useQuery({
    queryKey: ['item-saved', contentType, contentId],
    queryFn: async () => {
      if (!contentType || !contentId) return false;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('moodboard_items')
        .select('id, moodboard_id')
        .eq('content_type', contentType)
        .eq('content_id', contentId)
        .limit(1);

      if (error) throw error;
      return (data && data.length > 0) ? true : false;
    },
    enabled: !!contentType && !!contentId,
  });
}

// Remove item from all moodboards
export function useRemoveFromMoodboards() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { contentType: string; contentId: string }) => {
      const { error } = await supabase
        .from('moodboard_items')
        .delete()
        .eq('content_type', params.contentType)
        .eq('content_id', params.contentId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['moodboards'] });
      queryClient.invalidateQueries({ queryKey: ['item-saved', variables.contentType, variables.contentId] });
      toast({
        title: 'Removed from moodboards',
        description: 'Item has been unsaved',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to remove item',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
